/*
 **  FIS AI Code Assistant Policy Compliance
 **  This file has been developed partially with the assistance of GitHub Copilot
 **  AI coding assistance started: 05/14/2025
 **  Developer notes:
 */

import { ClickAwayListener } from '@mui/material';
import moment from 'moment';
import React from 'react';
import 'react-day-picker/lib/style.css';
import { Filter } from 'react-table';
import styled from 'styled-components';
import YearMonthForm from './components/YearMonthForm';
import { LoadingWithOverlay } from '@virtus/components/Loading';
import { LoadingIconSizes, LoadingIconType } from '@virtus/components/LoadingIcon/LoadingIcon';

const DayPicker = React.lazy(() => import('react-day-picker'));

export interface DateFilterProps {
  onChange: (e: any) => void;
}

const checkDate = (rowValue: Date | null, from: Date | null, until: Date | null) => {
  if (rowValue && from && until) {
    return rowValue >= from && rowValue <= until;
  }
  if (rowValue && from && !until) {
    return rowValue >= from;
  }
  if (rowValue && !from && until) {
    return rowValue <= until;
  }
  return;
};

export const dateFilterMethod = (filter: Filter, row: any) => {
  const from: Date | null = filter.value.from ? moment(filter.value.from).toDate() : null;
  const until: Date | null = filter.value.until ? moment(filter.value.until).toDate() : null;
  if (row[filter.id] && !row.subRows && typeof row[filter.id] === 'string') {
    const rowValue: Date | null = moment(row[filter.id]).isValid() ? moment(row[filter.id]).toDate() : null;
    return checkDate(rowValue, from, until);
  }
  if (row._aggregated) {
    return row._subRows.filter((subrow: any) => {
      if (subrow[filter.id]) {
        const subRowValue: Date | null = moment(subrow[filter.id]).isValid()
          ? moment(subrow[filter.id]).toDate()
          : null;
        if (subRowValue) {
          return checkDate(subRowValue, from, until);
        }
      }
      return false;
    });
  }
  return false;
};

export interface DateFilterProps {
  onChange: (value: any) => void;
}

export const formatDate = (date: Date): string => new Date(date).toISOString().substring(0, 10);

const currentYear = new Date().getFullYear();
const fromMonth = new Date(currentYear - 20);
const toMonth = new Date(currentYear + 20, 11);

const modifiersStyles = {
  selected: {
    color: 'white',
  },
};

export const getSelectedDays = (date?: Date) => (date ? [new Date(date)] : []);

export default class DateFilter extends React.Component<DateFilterProps> {
  state = { month: new Date(), showFrom: false, showUntil: false, fromDate: undefined, untilDate: undefined };

  emitDateChange = () =>
    this.props.onChange({
      from: this.state.fromDate,
      until: this.state.untilDate,
    });

  handleYearMonthChange = (month: Date) => this.setState({ month });

  handleFromChange = (date: Date) =>
    this.setState({ showUntil: false, showFrom: false, fromDate: formatDate(date) }, () => this.emitDateChange());

  handleUntilChange = (date: Date) =>
    this.setState({ showUntil: false, showFrom: false, untilDate: formatDate(date) }, () => this.emitDateChange());

  toggleFrom = () => this.setState({ showFrom: !this.state.showFrom, showUntil: false });

  toggleUntil = () =>
    this.setState({
      showFrom: false,
      showUntil: !this.state.showUntil,
    });

  onClickAway = () => this.setState({ showFrom: false, showUntil: false });

  render() {
    return (
      <ClickAwayListener data-testid="click-away-listener" onClickAway={this.onClickAway}>
        <DateFilterWrapper>
          <CalendarWrapper>
            <CalendarLabel data-testid="from-label" onClick={this.toggleFrom}>
              {this.state.fromDate || 'From Date '}
            </CalendarLabel>
            {this.state.showFrom && (
              <Calendar data-testid="from-calendar">
                <React.Suspense
                  fallback={
                    <LoadingWithOverlay
                      overlayColor="rgba(0, 0, 0, 0.5)"
                      show={true}
                      size={LoadingIconSizes.small}
                      type={LoadingIconType.Virtus}
                    />
                  }
                >
                  <DayPicker
                    month={this.state.month}
                    fromMonth={fromMonth}
                    selectedDays={getSelectedDays(this.state.fromDate)}
                    toMonth={toMonth}
                    onDayClick={this.handleFromChange}
                    modifiersStyles={modifiersStyles}
                    captionElement={({ date, localeUtils }: any) => (
                      <YearMonthForm
                        fromMonth={fromMonth}
                        toMonth={toMonth}
                        date={date}
                        localeUtils={localeUtils}
                        onChange={this.handleYearMonthChange}
                      />
                    )}
                  />
                </React.Suspense>
              </Calendar>
            )}
          </CalendarWrapper>
          <CalendarWrapper>
            <CalendarLabel data-testid="until-label" onClick={this.toggleUntil}>
              {this.state.untilDate || 'Until Date '}
            </CalendarLabel>
            {this.state.showUntil && (
              <Calendar data-testid="until-calendar">
                <React.Suspense
                  fallback={
                    <LoadingWithOverlay
                      overlayColor="rgba(0, 0, 0, 0.5)"
                      show={true}
                      size={LoadingIconSizes.small}
                      type={LoadingIconType.Virtus}
                    />
                  }
                >
                  <DayPicker
                    month={this.state.month}
                    fromMonth={fromMonth}
                    selectedDays={getSelectedDays(this.state.untilDate)}
                    toMonth={toMonth}
                    onDayClick={this.handleUntilChange}
                    modifiersStyles={modifiersStyles}
                    captionElement={({ date, localeUtils }: any) => (
                      <YearMonthForm
                        fromMonth={fromMonth}
                        toMonth={toMonth}
                        date={date}
                        localeUtils={localeUtils}
                        onChange={this.handleYearMonthChange}
                      />
                    )}
                  />
                </React.Suspense>
              </Calendar>
            )}
          </CalendarWrapper>
        </DateFilterWrapper>
      </ClickAwayListener>
    );
  }
}

// Styled Components
const DateFilterWrapper = styled.div`
  display: flex;
  width: 100%;
`;

const CalendarWrapper = styled.div`
  flex: 1;

  .DayPicker:not(.DayPicker--interactionDisabled)
    .DayPicker-Day:not(.DayPicker-Day--disabled):not(.DayPicker-Day--selected):not(.DayPicker-Day--outside):hover {
    background: #0d83c9;
    color: white;
  }
`;

const CalendarLabel = styled.div`
  border: 1px solid rgba(0, 0, 0, 0.1) !important;
  background-color: #fff !important;
  padding: 4px 5px !important;
  font-size: inherit !important;
  border-radius: 2px !important;
  font-weight: normal !important;
  outline: none !important;
  cursor: pointer !important;
  height: 28px;
`;

const Calendar = styled.div`
  position: absolute;
  background-color: white;
  margin-top: 5px;
  box-shadow: -1px 8px 12px -5px rgba(0, 0, 0, 0.2);
`;
